import {
	FirmwareUpdateProgress,
	FirmwareUpdateStage,
	FirmwareUpdateStatus,
	InventoryDevice,
	MissingFeatureType
} from '@shure/cloud/shared/models/devices';
import { mapDurationFromSysApi } from '@shure/shared/angular/data-access/system-api/mappers';

import { DeviceStatusService } from '../../api/device-status.service';
import { InventoryDeviceFragment } from '../graphql/generated/cloud-sys-api';

import { mapBatteryStateFromSysApi } from './map-battery-state';
import { mapCloudGatewayFromSysApi } from './map-cloud-gateway';
import { mapDeviceAuthenticationFromSysApi } from './map-device-authentication';
import { mapExtendedWarrantyFromSysApi } from './map-extended-warranty';
import { mapFirmwarePackagesFromSysApi } from './map-firmware-packages';
import { mapLicenseV3FromSysApi } from './map-license-v3';
import { mapScheduledJobRequestFromSysApi } from './map-scheduled-job-request';
import {
	deviceFragmentToMicStateFeature,
	mapCompatibilityLevelFromSysApi,
	mapDeviceModelFromSysApi,
	mapFeatureValueFromSysApi,
	mapHostedFirmwareVersionFromSysApi
} from './utils';

export function mapInventoryDeviceFromSysApi(
	device: InventoryDeviceFragment,
	statusService: DeviceStatusService
): InventoryDevice {
	const name = mapFeatureValueFromSysApi(device.features.name?.name, '');
	const mute = {
		muted: device.features.audioMute?.muted ?? false,
		isMissing: !device.description.features.audioMute.supported
	};

	const batteryLevel = {
		percentage: device.features.batteryLevel?.percentage ?? 0,
		currentState: mapBatteryStateFromSysApi(device.features.batteryLevel?.status.currentState),
		targetState: mapBatteryStateFromSysApi(device.features.batteryLevel?.status.targetState),
		timeToTargetState: mapDurationFromSysApi(device.features.batteryLevel?.status.timeToTargetState),
		isMissing: !device.description.features.batteryLevel?.supported
	};

	const controlNetwork = {
		ipAddress: device.features.controlNetwork
			? mapFeatureValueFromSysApi(device.features.controlNetwork.interface.ipAddress)
			: mapFeatureValueFromSysApi(device.protocol.dmp?.ipAddress),
		macAddress: mapFeatureValueFromSysApi(device.features.controlNetwork?.interface.macAddress),
		isMissing: !device.description.features.controlNetwork.supported
	};

	const firmware = {
		version: mapFeatureValueFromSysApi(device.features.firmware?.version),
		valid: device.features.firmware?.valid ?? true,
		isUpToDate: true,
		isMissing: !device.description.features.firmware.supported
	};

	const tmpFeat = device.features.hostedUpdateProgress;
	const hostedUpdateProgress = {
		package: {
			key: tmpFeat?.firmwarePackageKey ?? '',
			version: tmpFeat?.firmwarePackageVersion ?? '0.0.0.0'
		},
		percentComplete: tmpFeat?.stageProgressPercentage ?? 100,
		stage: tmpFeat?.updateStage ?? FirmwareUpdateStage.Idle,
		status: tmpFeat?.updateStatus ?? FirmwareUpdateStatus.Success,
		isMissing: !device.description.features.hostedUpdateProgress?.supported
	};

	const hostedFirmware = {
		key: mapFeatureValueFromSysApi(device.features.hostedFirmware?.key, ''),
		version: mapHostedFirmwareVersionFromSysApi(device.features.hostedFirmware?.version),
		isMissing: !device.description.features.hostedFirmware?.supported
	};

	const identify = {
		identifying: device.features.identify?.identifying || false,
		isMissing: !device.description.features.identify.supported
	};

	const licenseV3 = device.description.features.licenseV3?.supported
		? {
				license: mapLicenseV3FromSysApi(device.features.licenseV3),
				isMissing: !device.features.licenseV3
		  }
		: undefined;

	const micStatus = deviceFragmentToMicStateFeature(device);

	const serialNumber = {
		serialNumber: mapFeatureValueFromSysApi(device.features.serialNumber?.serialNumber),
		isMissing: !device.description.features.serialNumber.supported
	};

	const updateProgress: FirmwareUpdateProgress & MissingFeatureType = {
		package: {
			key: device.features.updateProgress?.firmwarePackageKey ?? '',
			version: device.features.updateProgress?.firmwarePackageVersion ?? '0.0.0.0'
		},
		percentComplete: device.features.updateProgress?.deviceProgressPercentage ?? 100,
		stage: device.features.updateProgress?.updateStage ?? FirmwareUpdateStage.Idle,
		status: device.features.updateProgress?.updateStatus ?? FirmwareUpdateStatus.Success,
		isMissing: !device.description.features.updateProgress.supported
	};

	const availablePackages = {
		...mapFirmwarePackagesFromSysApi(device.features.availablePackages),
		isMissing: !device.description.features.availablePackages.supported
	};

	const proxiedDevices = {
		proxiedDevices: (device.features.proxiedDevices?.proxiedDevices ?? []).map((proxiedDevice) => proxiedDevice),
		isMissing: !device.description.features.proxiedDevices?.supported
	};

	const room = {
		room: {
			name: mapFeatureValueFromSysApi(device.features.room?.room?.name)
		},
		isMissing: !device.description.features.room.supported
	};

	const tags = {
		tags: device.features.tags?.tags ?? [],
		isMissing: !device.description.features.tags?.supported
	};

	const authentication = {
		...mapDeviceAuthenticationFromSysApi(device.features.authentication),
		isMissing: !device.description.features.authentication?.supported
	};

	const cloudGateway = {
		...mapCloudGatewayFromSysApi(device.features.cloudGateway),
		isMissing: !device.description.features.cloudGateway?.supported
	};

	const extendedWarranty = device.description.features.extendedWarranty?.supported
		? {
				...mapExtendedWarrantyFromSysApi(device.features.extendedWarranty),
				isMissing: !device.features.extendedWarranty
		  }
		: undefined;

	const pendingFirmwareJobRequest = device.features.scheduledJobs?.firmwareJobRequests.pending;
	const scheduledJobs = device.description.features.scheduledJobs?.supported
		? {
				scheduledJobRequests: pendingFirmwareJobRequest
					? [mapScheduledJobRequestFromSysApi(pendingFirmwareJobRequest)]
					: [],
				isMissing: !device.features.scheduledJobs
		  }
		: undefined;

	const iotRoomProxy = device.description.features.iotRoomProxy?.supported
		? {
				enabled: device.features.iotRoomProxy?.enabled ?? false,
				isMissing: !device.features.iotRoomProxy
		  }
		: undefined;

	const unaugmentedDevice: Omit<InventoryDevice, 'conditions'> = {
		id: device.id,
		hardwareId: device.hardwareId,
		name,
		model: mapDeviceModelFromSysApi(device.interface.model ?? ''),
		virtual: device.virtual,
		status: device.status,
		compatibility: mapCompatibilityLevelFromSysApi(device.compatibility),
		manufacturer: device.manufacturer,
		features: {
			availablePackages,
			authentication,
			batteryLevel,
			cloudGateway,
			controlNetwork,
			extendedWarranty,
			firmware,
			hostedFirmware,
			hostedUpdateProgress,
			identify,
			iotRoomProxy,
			licenseV3,
			micStatus,
			mute,
			proxiedDevices,
			room,
			scheduledJobs,
			serialNumber,
			tags,
			updateProgress
		}
	};

	return {
		...unaugmentedDevice,
		conditions: statusService.conditionsFor(<InventoryDevice>unaugmentedDevice)
	};
}
